<template>
  <v-form ref="form"
          method="post"
          enctype="multipart/form-data"
          @submit.prevent="onSubmit">
    <v-card-text>
      <v-autocomplete label="Prediction"
                      :items="predictions"
                      item-text="name"
                      item-value="uuid"
                      v-model="uuid"
                      required
                      clearable />
      <v-autocomplete label="Group (optional)"
                      :items="group_options"
                      item-text="name"
                      item-value="id"
                      v-model="config.group"
                      hint="Fills in this group's own wells, enabled targets, and a starting model below -- still freely editable afterward"
                      persistent-hint
                      clearable />
      <v-row>
        <v-col>
          <v-autocomplete label="Targets"
                          :items="target_options"
                          v-model="config.targets"
                          :rules="rules.minimum"
                          multiple
                          required
                          clearable />
        </v-col>
        <v-col>
          <!-- Every model shares this one technique -- ARIES tells them
               apart by qualifier (auto-derived from method below). -->
          <v-autocomplete v-model="config.technique"
                          :items="techniques"
                          item-text="name"
                          item-value="slug"
                          label="Technique"
                          :rules="rules.required"
                          required
                          clearable />
        </v-col>
        <v-col>
          <v-select label="Chosen ID"
                    :items="well_id_formats"
                    v-model="config.well_id_format"
                    :rules="rules.required"
                    required />
        </v-col>
      </v-row>
      <!-- Unlike ComboCurve (one method), ARIES supports several models
           side by side -- each one's qualifier is derived from its own
           slug (utils/aries.js), never user-entered. -->
      <v-autocomplete label="Models"
                      :items="method_options"
                      v-model="config.methods"
                      :rules="rules.minimum"
                      multiple
                      required
                      clearable
                      hint="One model per selection, each tagged with its own auto-generated ARIES qualifier"
                      persistent-hint />
      <v-alert v-if="config.methods.length > 0"
               dense
               text
               type="info"
               class="mt-2 mb-4">
        Qualifiers: {{ qualifierPreview }}
      </v-alert>
      <v-alert v-if="termination_display"
               dense
               text
               type="info"
               class="mb-4">
        Terminal decline already applied to this prediction at creation: {{ termination_display }}
      </v-alert>

      <v-row class="align-center">
        <v-col cols="auto">
          <v-checkbox label="Terminate forecasts"
                      v-model="config.terminate"></v-checkbox>
        </v-col>
        <v-col cols="auto">
          <v-radio-group v-model="config.strategy"
                         row
                         :disabled="!config.terminate">
            <v-radio v-for="(option, o) in strategies"
                     :key="o"
                     :label="option.text"
                     :value="option.value"
                     required
                     :disabled="!config.terminate" />
          </v-radio-group>
        </v-col>
        <v-col>
          <v-text-field v-model.number="config.limit"
                        :suffix="strategy_suffix"
                        class="mt-0 pt-0"
                        type="number"
                        :hint="strategy_hint"
                        hide-spin-buttons
                        persistent-hint
                        required
                        :disabled="!config.terminate" />
        </v-col>
      </v-row>

    </v-card-text>
    <v-card-actions>
      <v-btn depressed
             class="success"
             type="submit"
             :disabled="!isValid">Generate</v-btn>
      <v-btn text
             type="button"
             @click="$router.go(-1)">Cancel</v-btn>
    </v-card-actions>
  </v-form>
</template>
<script>
import Predictions from "@/api/predictions"
import { deriveAriesQualifier } from "@/utils/aries"
export default {
  name: "ExportAriesForm",
  props: {
    predictions: { type: Array, required: true },
    techniques: { type: Array, required: true },
    instance: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      uuid: null,
      prediction: null,
      strategies: [
        { text: 'Duration', value: 'duration', hint: 'Cut off forecasts after a certain number of months', unit: 'months' },
        { text: 'Production', value: 'production', hint: 'Cut off forecasts when monthly volume falls below a certain threshold', unit: 'bbl/m' },
      ],
      well_id_formats: ['API 10', 'API 14'],
      config: {
        group: null,
        // Set as a side effect of picking a Group below; null exports every well.
        wells: null,
        targets: [],
        methods: [],
        technique: null,
        well_id_format: 'API 10',
        terminate: false,
        strategy: null,
        limit: null,
      },
      rules: {
        required: [v => !!v || "Field is required"],
        minimum: [v => v.length > 0 || "Minimum one is required"],
      },
      // True while mounted() restores a saved export's config -- suppresses
      // "config.group"'s reset/reseed below, which would otherwise clobber
      // the just-restored targets/methods/technique.
      restoringInstance: false,
    }
  },
  mounted() {
    if (!this.isEmpty(this.instance)) {
      this.restoringInstance = true
      this.uuid = this.instance.prediction.uuid
      let cfg = this.instance.config || {}
      this.config = {
        group: cfg.group != null ? cfg.group : null,
        wells: cfg.wells || null,
        targets: cfg.targets || [],
        methods: (cfg.models || []).map(m => m.method),
        technique: (cfg.models && cfg.models[0] && cfg.models[0].technique) || null,
        well_id_format: cfg.well_id_format || 'API 10',
        terminate: cfg.terminate || false,
        strategy: cfg.strategy || null,
        limit: cfg.limit || null,
      }
      this.$nextTick(() => { this.restoringInstance = false })
    }
  },
  watch: {
    uuid: function (val) {
      if (val) {
        Predictions.retrieve(val, `{id,uuid,name}`).then(response => {
          this.prediction = response.data
        })
      } else {
        this.config.group = null
        this.config.wells = null
        this.config.targets = []
        this.config.methods = []
        this.config.technique = null
        this.config.well_id_format = 'API 10'
        this.config.terminate = false
        this.config.strategy = null
        this.config.limit = null
      }
    },
    "config.terminate": function (val) {
      if (!val) {
        this.config.strategy = null
        this.config.limit = null
      }
    },
    "config.group": function (val) {
      if (this.restoringInstance) return
      if (val) {
        // Seeds targets and a starting model from the group's current
        // settings, freely editable after. wells isn't seeded from
        // group.wells -- that snapshot is unreliable; the backend
        // re-derives it from the group's filters at generation time.
        let group = this.group_options.find(g => g.id == val)
        if (group) {
          this.config.targets = Object.entries(group.targets || {})
            .filter(([target, t]) => t.enabled && target != 'water')
            .map(([target]) => target)
          this.config.methods = [group.settings.method]
          this.config.technique = group.settings.technique
        }
      } else {
        this.config.targets = []
        this.config.methods = []
        this.config.technique = null
      }
    },
  },
  computed: {
    target_options() {
      if (!this.prediction) return []
      let targets = []
      for (let [key, value] of Object.entries(this.prediction.config.targets)) {
        if (value != null) targets.push({ text: this.capitalize(key), value: key })
      }
      // A selected group restricts choices to its own enabled targets.
      if (this.config.group) {
        let group = this.group_options.find(g => g.id == this.config.group)
        if (group) {
          let enabled = Object.entries(group.targets || {})
            .filter(([, t]) => t.enabled)
            .map(([target]) => target)
          targets = targets.filter(t => enabled.includes(t.value))
        }
      }
      // Water disabled for ARIES -- causes import issues on the receiving end.
      return targets.filter(t => t.value != 'water')
    },
    method_options() {
      if (!this.prediction) return []
      let methods = []
      this.prediction.methods.forEach(element => {
        methods.push({ text: element.name, value: element.slug })
      })
      return methods
    },
    // Every group on this prediction, not just locked ones.
    group_options() {
      if (!this.prediction) return []
      return this.prediction.groups || []
    },
    strategy_suffix() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.unit) return strategy.unit
      return ''
    },
    strategy_hint() {
      if (!this.config.strategy) return ''
      let strategy = this.strategies.find(s => s.value == this.config.strategy)
      if (strategy && strategy.hint) return strategy.hint
      return ''
    },
    // Read-only -- reflects the prediction's own config.termination; the
    // backend sources this automatically, nothing to re-enter here.
    termination_display() {
      if (!this.prediction) return null
      let termination = this.prediction.config.termination
      if (!termination || !termination.enabled) return null
      let parts = this.config.targets
        .filter(target => termination[target] != null)
        .map(target => `${this.capitalize(target)} ${termination[target]}%`)
      return parts.length > 0 ? parts.join(', ') : null
    },
    qualifierPreview() {
      return this.config.methods.map(m => deriveAriesQualifier(m, this.config.technique)).join(', ')
    },
    isValid() {
      let targets = !this.isEmpty(this.config.targets)
      let technique = !!this.config.technique
      let well_id_format = !!this.config.well_id_format
      let methods = this.config.methods.length > 0
      let terminate = !!this.config.terminate
      let strategy = !!this.config.strategy
      let limit = !!this.config.limit

      if (!(targets && technique && well_id_format && methods)) return false
      if (terminate && !strategy) return false
      if (terminate && strategy && !limit) return false

      return true
    },
  },
  methods: {
    onSubmit() {
      if (this.$refs.form.validate() && this.isValid) {
        let data = {
          prediction: this.prediction.id,
          config: {
            format: 'aries',
            // Bookkeeping only (which group seeded these fields, for
            // re-editing) -- never read at generation time.
            group: this.config.group,
            wells: this.config.wells,
            targets: this.config.targets,
            models: this.config.methods.map(method => ({
              method,
              technique: this.config.technique,
              qualifier: deriveAriesQualifier(method, this.config.technique),
            })),
            well_id_format: this.config.well_id_format,
            terminate: this.config.terminate,
            strategy: this.config.strategy,
            limit: this.config.limit,
          }
        }
        this.$emit("submitted", data)
      }
    },
    isEmpty(obj) {
      return Object.keys(obj).length === 0
    },
    capitalize(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },
  }
}
</script>
